import {DialogBody, DialogDismiss, DialogFooter} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Text} from '@/components/ui/text/Text'
import {useTranslation} from '@/translations/i18n'
import {ScaleType} from '../../types'
import {barthelScaleMainQuestions, scaleTypeToLabel} from '../../utils'
import {StyledDialog, StyledDialogHeader, StyledTotalScore} from './style'
import {Button} from '@/components/ui/button/Button'
import {zodResolver} from '@hookform/resolvers/zod'
import {Controller, useForm} from 'react-hook-form'
import {StoreBarthelEvaluationScaleFormSchema} from '../../formSchemas'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {SelectProvider} from '@ariakit/react'
import {SelectItem, SelectList, SelectPopover, SelectTrigger} from '@/components/ui/select-atoms/SelectAtoms'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Badge} from '@/components/ui/badge/Badge'

export const BarthelScaleDialog = ({
    defaultValues,
    evaluationScaleCount,
    onConfirm
}: {
    defaultValues?: StoreBarthelEvaluationScaleFormSchema
    evaluationScaleCount: number
    onConfirm: (values: StoreBarthelEvaluationScaleFormSchema, totalScore: number) => void
}) => {
    const {t} = useTranslation()
    const {
        control,
        handleSubmit,
        formState: {isValid},
        watch,
        setValue
    } = useForm<StoreBarthelEvaluationScaleFormSchema>({
        resolver: zodResolver(StoreBarthelEvaluationScaleFormSchema),
        defaultValues: {
            questions: defaultValues?.questions,
            type: ScaleType.enum.barthel_index
        }
    })
    const totalScore = Object.values(watch('questions') ?? {}).reduce(
        (sum: number, value) => sum + Number(value ?? 0),
        0
    )

    const onSubmit = handleSubmit(values =>
        onConfirm(
            {
                ...values,
                questions: {...values.questions, weelchair_usage: values.questions.weelchair_usage ?? undefined}
            },
            totalScore
        )
    )

    return (
        <StyledDialog width="800px">
            <StyledDialogHeader>
                <Text render="h1" size="textMd" weight="500">
                    {t('projects:x_evaluation_scale', {count: evaluationScaleCount})}
                </Text>
                <Text render="h1" size="textMd" weight="500">
                    {t(scaleTypeToLabel[ScaleType.enum.barthel_index])}
                </Text>
            </StyledDialogHeader>
            <DialogBody>
                {barthelScaleMainQuestions.map((question, index) => (
                    <Flexbox key={index} direction="column" gap={1.5}>
                        <Text render="h2" size="textSm" weight="500">
                            {`${index + 1}. ${t(question.title)}`}
                        </Text>
                        <Controller
                            control={control}
                            name={`questions.${question.name}`}
                            render={({field, fieldState}) => (
                                <SelectProvider
                                    defaultValue=""
                                    value={field.value?.toString() ?? ''}
                                    setValue={value => {
                                        if (
                                            field.name == 'questions.mobility' &&
                                            value != '0' &&
                                            watch('questions.weelchair_usage')
                                        ) {
                                            setValue('questions.weelchair_usage', null)
                                        }
                                        field.onChange(value)
                                    }}
                                >
                                    <SelectTrigger
                                        disabled={
                                            field.name == 'questions.weelchair_usage' &&
                                            watch('questions.mobility') != '0'
                                        }
                                        fullWidth
                                        placeholder={t('commons:select')}
                                        displayValue={value => {
                                            const label = question.options.find(option => option.value == value)?.label
                                            return label ? t(label) : value
                                        }}
                                    />
                                    {fieldState.error && <InputHelpText error={t('errors:required')} />}
                                    {field.name == 'questions.weelchair_usage' && (
                                        <InputHelpText helpText={t('projects:questions_wheelchair_usage_helptext')} />
                                    )}
                                    <SelectPopover>
                                        <SelectList>
                                            {question.options.map(option => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    {t(option.label)}{' '}
                                                    <Badge size="sm">
                                                        {t('commons:score')}: {option.value}
                                                    </Badge>
                                                </SelectItem>
                                            ))}
                                        </SelectList>
                                    </SelectPopover>
                                </SelectProvider>
                            )}
                        />
                    </Flexbox>
                ))}
                <StyledTotalScore>
                    {t('commons:total_score')} {totalScore}
                </StyledTotalScore>
            </DialogBody>
            <DialogFooter>
                <DialogDismiss render={<Button variant="secondary" />}>{t('commons:close')}</DialogDismiss>
                <DialogDismiss
                    onClick={event => {
                        if (!isValid) {
                            event.preventDefault()
                        }
                        onSubmit()
                    }}
                    render={<Button variant="primaryNeutral" />}
                >
                    {t('commons:confirm')}
                </DialogDismiss>
            </DialogFooter>
        </StyledDialog>
    )
}
